// El agente de ESE hilo, al lado de la conversación con Santi (entrega 3, construccion.md §«Entrega 3»; Pau, 04-11: «no
// tengo al agente encargado de ese contrato para hablar y discutir cosas… no quiero que el agente trabaje solo, siempre
// con mi ayuda»). Sustituye al cajón «Tu agente» (uno para todo, a demanda) de las entregas 1 y 2.
// - Qué agente: el del contrato (el que lo escaló, o el que le puso la oficina); en «Chat con Santi», el de General; en
//   «Pedir algo», el que preparó el pedido (si vino de «Revisar») o el de General.
// - Sin agente vivo: al abrirlo Pau, la web pide uno (`POST /temas/:hilo/agente`, una vez por hilo) y enseña «Poniendo un
//   agente…» mientras el servidor lo contrata con el expediente del contrato. Ponerlo es solo de Pau: un socio ve «Aún
//   no tiene agente…». El servidor contesta `agentId: null` para un contrato cerrado sin agente (no se pone ninguno).
// - Su chat es el de la oficina de siempre (ConversacionOficina + Compositor, con micrófono y adjuntos), con su nombre
//   arriba. Al final, su «Propuesta para Santi» (PropuestaEnElChat) y su «Pedir algo» preparado (PedidoEnElChat).
// - Escritorio: una columna fija a la derecha. iPad (y móvil): encima de la conversación, con su ✕ (`encima`).
// Socio (02-11): lo ve igual, con el título «El agente de Pau», y le escribe como a cualquiera (le llega firmado).
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useAppStore } from '../../../stores/app.store';
import { Compositor } from '../chat/Compositor';
import { ConversacionOficina } from '../chat/ConversacionOficina';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { mensajeError } from '../errores';
import { useMarcarLeido } from '../leidos';
import { quien } from '../marco/estado';
import { useOficinaStore } from '../store';
import { Boton, BotonIcono, IconoEncargo } from '../ui';
import { pelotasApi } from './api';
import { AvatarAgente } from './piezas';
import { PedidoEnElChat } from './PedidoEnElChat';
import { PropuestaEnElChat } from './PropuestaEnElChat';
import { pedirHilo, useHablaComoPau, usePelotasStore } from './store';
import type { HiloAbierto } from './tipos';

/**
 * El agente que toca para lo que enseña la ventana, como lo saben la lista y los hilos (null = aún no tiene); `cargado`:
 * ya se sabe (sin esto no se pide uno: aún no ha llegado la lista o el hilo).
 */
export function useAgenteConocido(hilo: HiloAbierto): { agentId: string | null; cargado: boolean } {
  const agentId = usePelotasStore((s) => {
    if (typeof hilo === 'number') return (s.hilos[hilo]?.contrato ?? s.lista?.contratos.find((c) => c.temaId === hilo))?.agentId ?? null;
    if (hilo === 'pedir' && s.pedidoAbierto) return s.pedidoAbierto;
    return s.general?.agentId ?? null;
  });
  const cargado = usePelotasStore((s) => {
    if (typeof hilo === 'number') return !!(s.hilos[hilo] ?? s.lista?.contratos.find((c) => c.temaId === hilo));
    return (hilo === 'pedir' && s.pedidoAbierto !== null) || s.general !== null;
  });
  return { agentId, cargado };
}

export function AgenteDelHilo({ hilo, alCerrar, encima = false, className }: { hilo: HiloAbierto; alCerrar?: () => void; encima?: boolean; className?: string }) {
  const comoPau = useHablaComoPau();
  const destino: number | 'general' = typeof hilo === 'number' ? hilo : 'general';
  const conocido = useAgenteConocido(hilo);
  const [puesto, setPuesto] = useState<{ hilo: HiloAbierto; agentId: string } | null>(null);
  const agentId = (puesto?.hilo === hilo ? puesto.agentId : null) ?? conocido.agentId;
  const hidratada = useOficinaStore((s) => s.hidratada);
  const contratado = useOficinaStore((s) => (agentId ? s.contratados[agentId] : undefined));
  const [poniendo, setPoniendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intento, setIntento] = useState(0);
  const pedidos = useRef(new Set<string>());

  // El chat con Santi (y «Pedir algo») van con el agente de General: si aún no se ha leído, se lee.
  useEffect(() => {
    if (typeof hilo !== 'number' && !usePelotasStore.getState().general) pedirHilo('general');
  }, [hilo]);

  // Sin agente vivo (ninguno, o el suyo ya no está en la oficina): la oficina le pone uno. Una vez por hilo.
  const sinAgente = conocido.cargado && (!agentId || (hidratada && !contratado));
  const [ninguno, setNinguno] = useState(false);
  useEffect(() => {
    const k = String(destino);
    if (!comoPau || !sinAgente || pedidos.current.has(k)) return;
    pedidos.current.add(k);
    setPoniendo(true);
    setError(null);
    pelotasApi
      .agente(destino)
      .then(async (r) => {
        if (!r.agentId) {
          setNinguno(true);
          return;
        }
        setPuesto({ hilo, agentId: r.agentId });
        await useOficinaStore.getState().hidratar();
        pedirHilo(hilo === 'pedir' ? 'general' : hilo);
        void usePelotasStore.getState().cargarLista();
      })
      .catch((e: unknown) => setError(mensajeError(e)))
      .finally(() => setPoniendo(false));
  }, [comoPau, sinAgente, destino, hilo, intento]);

  const titulo = contratado ? quien(contratado) : t(comoPau ? 'oficinaV2.pelotas.agente.titulo' : 'oficinaV2.pelotas.agente.dePau');
  const sub = !comoPau
    ? t('oficinaV2.pelotas.agente.dePau')
    : t(typeof hilo === 'number' ? 'oficinaV2.pelotas.agente.subContrato' : hilo === 'pedir' ? 'oficinaV2.pelotas.agente.subPedir' : 'oficinaV2.pelotas.agente.subGeneral');
  const aside = useRef<HTMLElement>(null);

  return (
    <aside
      ref={aside}
      className={cn('flex h-full min-h-0 min-w-0 flex-col border-l border-oficina-acento/20 bg-oficina-tuyo', className)}
      aria-label={titulo}
      data-testid="tu-agente"
      data-agente={agentId ?? undefined}
      data-encima={encima ? 'true' : undefined}
    >
      <header className="flex h-16 shrink-0 items-center gap-3 border-b border-oficina-linea bg-oficina-tuyo-oscuro pl-4 pr-2">
        {/* El icono de su encargo, como en toda la oficina (Pau, 30-09); el robot mientras no hay agente. */}
        {contratado ? <IconoEncargo icono={contratado.icono} semilla={contratado.semillaAvatar} tamano={40} /> : <AvatarAgente tamano={40} />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-o-cuerpo font-semibold text-oficina-texto" data-testid="tu-agente-titulo">
            {titulo}
          </p>
          <p className="truncate text-o-sub text-oficina-texto2" data-testid="tu-agente-sub">
            {sub}
          </p>
        </div>
        {alCerrar ? <BotonIcono icono={X} etiqueta={t('oficinaV2.pelotas.agente.cerrar')} onClick={alCerrar} data-testid="tu-agente-cerrar" /> : null}
      </header>
      {error ? (
        <div className="m-auto flex max-w-[300px] flex-col items-center gap-3 px-6 text-center" data-testid="agente-error">
          <p className="text-o-texto text-oficina-texto2">{t('oficinaV2.pelotas.agente.noSePuso')}</p>
          <Boton
            variante="secundario"
            onClick={() => {
              // Vuelve a pedirlo el efecto (sinAgente sigue siendo true).
              pedidos.current.delete(String(destino));
              setError(null);
              setIntento((n) => n + 1);
            }}
            data-testid="agente-reintentar"
          >
            {t('oficinaV2.pelotas.acto.volverAProbar')}
          </Boton>
        </div>
      ) : ninguno || (!comoPau && sinAgente) ? (
        <p className="m-auto max-w-[300px] px-6 text-center text-o-texto text-oficina-texto3" data-testid="agente-ninguno">
          {t('oficinaV2.pelotas.agente.sinAgente')}
        </p>
      ) : poniendo || (sinAgente && !contratado) ? (
        <p className="m-auto text-o-texto text-oficina-texto3" data-testid="agente-poniendo">
          {t('oficinaV2.pelotas.agente.poniendo')}
        </p>
      ) : !contratado ? (
        <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.pelotas.agente.cargando')}</p>
      ) : (
        <CuerpoAgente
          c={contratado}
          hilo={typeof hilo === 'number' || hilo === 'general' ? hilo : null}
          // La única caja de escribir de esta columna es la del agente.
          alCambiar={() => aside.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus()}
        />
      )}
    </aside>
  );
}

/** El chat del agente, como en su ventana: carga su conversación y su expediente al abrirse; lo leído se marca. */
function CuerpoAgente({ c, hilo, alCambiar }: { c: ContratadoDTO; hilo: number | 'general' | null; alCambiar: () => void }) {
  const agentId = c.agentId;
  const agent = useAppStore((s) => s.agents[agentId]);
  const messages = useAppStore((s) => s.messagesByAgent[agentId] ?? null);
  const loadMessages = useAppStore((s) => s.loadMessages);
  const abrirChat = useOficinaStore((s) => s.abrirChat);
  const expediente = useOficinaStore((s) => s.expedientes[agentId]);
  const [error, setError] = useState<string | null>(null);
  useMarcarLeido(c);
  useEffect(() => {
    abrirChat(agentId);
    return () => abrirChat(null);
  }, [abrirChat, agentId]);
  useEffect(() => {
    let vivo = true;
    setError(null);
    loadMessages(agentId).catch((e: unknown) => vivo && setError(mensajeError(e)));
    return () => {
      vivo = false;
    };
  }, [agentId, loadMessages]);
  return (
    <>
      <div className="relative flex min-h-0 flex-1 flex-col [&_[data-cola=agente]]:text-oficina-tuyo-globo [&_[data-globo=agente]]:bg-oficina-tuyo-globo">
        {error && messages === null ? (
          <p className="m-auto px-6 text-center text-o-texto text-oficina-error">{error}</p>
        ) : !agent || messages === null ? (
          <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.pelotas.agente.cargando')}</p>
        ) : (
          <ConversacionOficina
            contratado={c}
            mensajes={messages}
            traducciones={expediente?.ficha?.traducciones}
            resumenes={expediente?.ficha?.resumenes}
            pie={
              <>
                {hilo !== null ? <PropuestaEnElChat hilo={hilo} alCambiar={alCambiar} /> : null}
                <PedidoEnElChat agentId={agentId} />
              </>
            }
          />
        )}
      </div>
      <div className="[&_[data-compositor]]:bg-oficina-tuyo-oscuro">
        <Compositor key={agentId} agentId={agentId} placeholder={t('oficinaV2.pelotas.agente.escribe')} />
      </div>
    </>
  );
}
